import { assign } from 'lodash-es';

const EVENT_IDS = {
  ACCOUNT_LOGIN: 'account_login',
  ACCOUNT_SIGN_UP: 'account_sign_up',
  BEGIN_CHECKOUT: 'begin_checkout',
  B2B_CART_FREEZE: 'cart-freeze',
  B2B_CART_UNFREEZE: 'cart-unfreeze',
  B2B_CART_API_FAILURE: 'cart-api-failure',
  CART_ACTIVATED: 'cart_activated',
  CART_SHOWN: 'cart_show',
  CART_HIDDEN: 'cart_hide',
  CART_CHANGED: 'cart-changed',
  CART_REMOVED_FROM_BAG: 'removed_bag_from_cart',
  CART_ADD: 'cart_add',
  CART_REMOVE: 'cart_remove',
  CART_TRACKER: 'cart_tracker',
  CART_DISCOUNT_CODE: 'cart_discount_code',
  CTA_CLICK: 'cta_click',
  GTM_V3_INIT: 'gtm_v3_init',
  GTM_V4_INIT: 'gtm_v4_init',
  LOCALE_CHANGED: 'locale-changed',
  LOGIN_ACTIVATED: 'login_activated',
  PRODUCT_LIST_FILTER: 'filter',
  PRODUCT_LIST_VIEW: 'product_list_view',
  PRODUCT_SELECT: 'product_select',
  PRODUCT_VIEW: 'product_view',
  PRODUCT_VIEW_GTM: 'product_view_gtm',
  SEARCH_ATTEMPT: 'search_attempt_tracker',
  SEARCH_COMPLETED: 'search',
  SEARCH_START: 'start_search',
  WISHLIST_ADD: 'wishlist_add',
  WISHLIST_REMOVE: 'wishlist_remove',
  WISHLIST_CART_ADD: 'wishlist_cart_add',
  THIRD_PART_VWO: 'third_part_vwo',
  DO_SEARCH: 'do_search'
};

const events = {
  /**
   * Dispatch accountLogin event
   * @param {String} userID
   * @param {String} customerType
   */
  accountLogin(userID, customerType) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.ACCOUNT_LOGIN, {
        detail: {
          userDetails: {
            userID,
            customerType
          }
        }
      })
    );
  },

  /**
   * Dispatch accountSignUp event
   * @param {String} userID
   * @param {String} customerType
   * @param {String} signUpMethod
   */
  accountSignUp(userID, customerType, signUpMethod = 'email') {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.ACCOUNT_SIGN_UP, {
        detail: {
          userDetails: {
            userID,
            signUpMethod,
            customerType
          }
        }
      })
    );
  },

  /**
   * Dispatch beginCheckout event
   */
  beginCheckout() {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.BEGIN_CHECKOUT, {
        detail: {}
      })
    );
  },

  /**
   * Dispatch localeChange event
   * @param value New locale, e.g. "EUR - Ireland"
   */
  localeChange(value) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.LOCALE_CHANGED, {
        detail: {
          locale: value
        }
      })
    );
  },

  /**
   * Dispatch loginActivate event
   */
  loginActivate() {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.LOGIN_ACTIVATED, {
        detail: {
          login: 'triggered'
        }
      })
    );
  },

  /**
   * Dispatch cartActivate event
   */
  cartActivated() {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.CART_ACTIVATED, {
        detail: {
          cart: 'triggered'
        }
      })
    );
  },

  /**
   * Dispatch cartShown event
   */
  cartShown() {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.CART_SHOWN, {
        detail: {
          cart: 'triggered'
        }
      })
    );

    document.querySelector('body').classList.add('cart-open');
  },

  /**
   * Dispatch cartShown event
   */
  cartHidden() {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.CART_SHOWN, {
        detail: {
          cart: 'triggered'
        }
      })
    );

    document.querySelector('body').classList.remove('cart-open');
  },

  /**
   * Dispatch cartChange event
   * @param cart Cart
   * @param inject Any custom information to inject
   */
  cartChange(cart = {}, inject = {}) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.CART_CHANGED, {
        detail: assign(
          {
            cart // @todo Do we really need?
          },
          inject || {}
        )
      })
    );
  },

  /**
   * Show cart
   */
  cartShow() {
    this.cartActivated();
    const cart = document.getElementById('cart');
    const cartContainer = document.getElementById('cart-container');
    if (cart && !cartContainer.classList.contains('cart-container--active')) {
      cart.click();
    }
  },

  /**
   * Dispatch removeBag event
   */
  removeBag() {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.CART_REMOVED_FROM_BAG, {
        detail: {}
      })
    );
  },

  /**
   * Dispatch addToCart event
   * @param {String} variantId Variant ID, with gid:// prefix
   * @param {Number} quantity Quantity, 1 default
   * @param {Object} options Any custom option
   */
  addToCart(variantId, quantity = 1, options = {}) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.CART_ADD, {
        detail: {
          variant_id: variantId,
          quantity,
          options
        }
      })
    );
  },

  /**
   * Dispatch removeFromCart event
   * @param variantId Variant ID, with gid:// prefix
   * @param {Number} quantity
   * @param options Any custom option
   */
  removeFromCart(variantId, quantity = 0, options = {}) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.CART_REMOVE, {
        detail: {
          variant_id: variantId,
          quantity,
          options
        }
      })
    );
  },

  /**
   * Dispatch cartTracker event
   * @param cart Current cart object
   */
  cartTracker(cart) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.CART_TRACKER, {
        detail: {
          cart
        }
      })
    );
  },

  /**
   * Dispatch discountCode event
   * @param code Discount code
   */
  cartDiscountCode(code) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.CART_DISCOUNT_CODE, {
        detail: {
          code
        }
      })
    );
  },

  /**
   * Dispatch b2bCartFreeze event
   */
  b2bCartFreeze() {
    window.dispatchEvent(new CustomEvent(EVENT_IDS.B2B_CART_FREEZE, {}));
  },

  /**
   * Dispatch b2bCartUnfreeze event
   */
  b2bCartUnfreeze() {
    window.dispatchEvent(new CustomEvent(EVENT_IDS.B2B_CART_UNFREEZE, {}));
  },

  /**
   * Dispatch b2bCartApiFailure event
   * @param error Error
   */
  b2bCartApiFailure(error) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.B2B_CART_API_FAILURE, { detail: { error } })
    );
  },

  /**
   * Dispatch gtmV3Init event
   */
  gtmV3Init() {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.GTM_V3_INIT, {
        detail: {}
      })
    );
  },

  /**
   * Dispatch gtmV4Init event
   */
  gtmV4Init() {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.GTM_V4_INIT, {
        detail: {}
      })
    );
  },

  /**
   * Dispatch filterProductList event
   * @param {String} type Filter type
   * @param {String} value Filter value
   */
  filterProductList(type, value) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.PRODUCT_LIST_FILTER, {
        detail: {
          type,
          value
        }
      })
    );
  },

  /**
   * Dispatch viewProductList event
   * @param {Object} products Product list
   */
  viewProductList(products = {}) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.PRODUCT_LIST_VIEW, {
        detail: {
          products
        }
      })
    );
  },

  /**
   * Dispatch selectProduct event
   * @param productId
   */
  selectProduct(productId = {}) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.PRODUCT_SELECT, {
        detail: {
          product_id: productId
        }
      })
    );
  },

  /**
   * Dispatch viewProduct event
   * @param productId Product ID, with gid:// prefix
   */
  viewProduct(productId) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.PRODUCT_VIEW, {
        detail: {
          product_id: productId
        }
      })
    );
  },

  /**
   * Dispatch viewProductGtm event
   * @param {String} productId Product ID, with gid:// prefix
   * @param {Object} otherData
   */
  viewProductGtm(productId, otherData = {}) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.PRODUCT_VIEW_GTM, {
        detail: {
          product_id: productId,
          other: otherData
        }
      })
    );
  },

  /**
   * Dispatch searchAttempt event
   * @param {String} searchItem
   */
  searchAttempt(searchItem) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.SEARCH_ATTEMPT, {
        detail: {
          search_terms: searchItem
        }
      })
    );
  },

  /**
   * Dispatch searchCompleted event
   * @param {String} searchTerm
   * @param {Number} resultCount
   */
  searchCompleted(searchTerm, resultCount) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.SEARCH_COMPLETED, {
        detail: {
          searchDetails: {
            searchTerm,
            resultCount
          }
        }
      })
    );
  },

  /**
   * Dispatch searchStart event
   * @param {String} searchTerm
   */
  searchStart(searchTerm) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.SEARCH_START, {
        detail: {
          searchTerm
        }
      })
    );
  },

  /**
   * Dispatch addtowishlist event
   * @param productId
   */
  wishlistAdd(productId) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.WISHLIST_ADD, {
        detail: {
          product_id: productId
        }
      })
    );
  },

  /**
   * Dispatch removefromwishlist event
   * @param productId
   */
  wishlistRemove(productId) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.WISHLIST_REMOVE, {
        detail: {
          product_id: productId
        }
      })
    );
  },

  /**
   * Dispatch addtobagfromwishlist event
   * @param variantId
   */
  wishlistCartAdd(variantId) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.WISHLIST_CART_ADD, {
        detail: {
          variant_id: variantId
        }
      })
    );
  },

  /**
   * Dispatch Third Part VWO init event
   */
  thirdPartVwoInit() {
    window.dispatchEvent(new CustomEvent(EVENT_IDS.THIRD_PART_VWO));
  },

  /**
   * Dispatch ctaClock init event
   */
  ctaClick(options = {}) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.CTA_CLICK, {
        detail: {
          options
        }
      })
    );
  },

  /**
   * Add listener for accountLogin event
   * @param {Function} callback
   */
  onAccountLogin(callback) {
    window.addEventListener(EVENT_IDS.ACCOUNT_LOGIN, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for accountSignUp event
   * @param {Function} callback
   */
  onAccountSignUp(callback) {
    window.addEventListener(EVENT_IDS.ACCOUNT_SIGN_UP, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for beginCheckout event
   * @param {Function} callback
   */
  onBeginCheckout(callback) {
    window.addEventListener(EVENT_IDS.BEGIN_CHECKOUT, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for localeChange event
   * @param {Function} callback
   */
  onLocaleChange(callback) {
    window.addEventListener(EVENT_IDS.LOCALE_CHANGED, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for loginActivate event
   * @param {Function} callback
   */
  onLoginActivate(callback) {
    window.addEventListener(EVENT_IDS.LOGIN_ACTIVATED, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for cartActivate event
   * @param {Function} callback
   */
  onCartActivated(callback) {
    window.addEventListener(EVENT_IDS.CART_ACTIVATED, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for cartShown event
   * @param {Function} callback
   */
  onCartShown(callback) {
    window.addEventListener(EVENT_IDS.CART_SHOWN, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for cartChanged event
   * @param {Function} callback
   */
  onCartChanged(callback) {
    window.addEventListener(EVENT_IDS.CART_CHANGED, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for removedBag event
   * @param {Function} callback
   */
  onRemovedBag(callback) {
    window.addEventListener(EVENT_IDS.CART_REMOVED_FROM_BAG, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for addToCart event
   * @param {Function} callback
   */
  onAddToCart(callback) {
    window.addEventListener(EVENT_IDS.CART_ADD, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for removeFromCart event
   * @param {Function} callback
   */
  onRemoveFromCart(callback) {
    window.addEventListener(EVENT_IDS.CART_REMOVE, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for cartTracker event
   * @param {Function} callback
   */
  onCartTracker(callback) {
    window.addEventListener(EVENT_IDS.CART_TRACKER, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for cartDiscountCode event
   * @param {Function} callback
   */
  onCartDiscountCode(callback) {
    window.addEventListener(EVENT_IDS.CART_DISCOUNT_CODE, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for b2bCartFreeze event
   * @param {Function} callback
   */
  onB2bCartFreeze(callback) {
    window.addEventListener(EVENT_IDS.B2B_CART_FREEZE, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for b2bCartUnfreeze event
   * @param {Function} callback
   */
  onB2bCartUnFreeze(callback) {
    window.addEventListener(EVENT_IDS.B2B_CART_UNFREEZE, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for b2bCartApiFailure event
   * @param {Function} callback
   */
  onB2bCartApiFailure(callback) {
    window.addEventListener(EVENT_IDS.B2B_CART_API_FAILURE, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for gtmV3Init event
   * @param {Function} callback
   */
  onGtmV3Init(callback) {
    window.addEventListener(EVENT_IDS.GTM_V3_INIT, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for gtmV4Init event
   * @param {Function} callback
   */
  onGtmV4Init(callback) {
    window.addEventListener(EVENT_IDS.GTM_V4_INIT, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for filterProductList event
   * @param {Function} callback
   */
  onFilterProductList(callback) {
    window.addEventListener(EVENT_IDS.PRODUCT_LIST_FILTER, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for viewProductList event
   * @param {Function} callback
   */
  onViewProductList(callback) {
    window.addEventListener(EVENT_IDS.PRODUCT_LIST_VIEW, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for productSelect event
   * @param {Function} callback
   */
  onProductSelect(callback) {
    window.addEventListener(EVENT_IDS.PRODUCT_SELECT, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for viewProduct event
   * @param {Function} callback
   */
  onViewProduct(callback) {
    window.addEventListener(EVENT_IDS.PRODUCT_VIEW, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for viewProductGtm event
   * @param {Function} callback
   */
  onViewProductGtm(callback) {
    window.addEventListener(EVENT_IDS.PRODUCT_VIEW_GTM, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for searchAttempt event
   * @param {Function} callback
   */
  onSearchAttempt(callback) {
    window.addEventListener(EVENT_IDS.SEARCH_ATTEMPT, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for searchCompleted event
   * @param {Function} callback
   */
  onSearchCompleted(callback) {
    window.addEventListener(EVENT_IDS.SEARCH_COMPLETED, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for searchStat event
   * @param {Function} callback
   */
  onSearchStart(callback) {
    window.addEventListener(EVENT_IDS.SEARCH_START, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for addtowishlist event
   * @param {Function} callback
   */
  onAddToWishlist(callback) {
    window.addEventListener(EVENT_IDS.WISHLIST_ADD, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for removefromwishlist event
   * @param {Function} callback
   */
  onRemoveFromWishlist(callback) {
    window.addEventListener(EVENT_IDS.WISHLIST_REMOVE, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for addtobagfromwishlist event
   * @param {Function} callback
   */
  onAddToCartFromWishlist(callback) {
    window.addEventListener(EVENT_IDS.WISHLIST_CART_ADD, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for third part VWO event
   * @param {Function} callback
   */
  onThirdPartVwoInit(callback) {
    window.addEventListener(EVENT_IDS.THIRD_PART_VWO, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  /**
   * Add listener for ctaClick event
   * @param {Function} callback
   */
  onCtaClick(callback) {
    window.addEventListener(EVENT_IDS.CTA_CLICK, (event) => {
      if (typeof callback === 'function') {
        callback(event);
      }
    });
  },

  doStart(searchTerm) {
    window.dispatchEvent(
      new CustomEvent(EVENT_IDS.DO_SEARCH, {
        detail: {
          searchTerm
        }
      })
    );
  }
};

export default events;
